/**
 * Part Wall: a demolition span drawn with TWO clicks along one structure wall.
 *
 * The first click fixes the wall and where the span starts, the second where
 * it ends. A span has no standard width the way a door does, so a one-click
 * tool at a default width meant placing a 3' piece and resizing it every time.
 *
 * Everything is measured as `t`, the distance along the wall's own axis from
 * its centre (`getStructureWallAxis`). Between the clicks the cursor may
 * wander off the wall: its position is projected back onto that axis and held
 * between the wall's two ends, so a span can never turn a corner.
 *
 * Each end snaps — within `PART_WALL_SNAP_PX` on screen — to where something
 * real is: the wall's own ends, the ends of every room wall on it (the faces
 * of the walls that meet it), and the edges of the doors, windows and
 * openings it holds. So "open up to the corner" and "widen this doorway" land
 * exactly, and the span can tell when it covers a whole partition
 * (`roomMergeService.findJoinForPartWall`).
 *
 * Pure: no React, no canvas.
 */
import { alongWallSpan } from '../components/3d/geometry/wallFaceCarve';
import {
  getStructureWallAxis,
  projectOntoAxis,
  roomWallsForStructureWall,
  roomWallAxisSpan,
  spanToOpening,
} from './wholeWallDemo';

/**
 * How near on screen an end must come to a wall end, a corner or an opening's
 * edge to land on it. Also the shortest span two clicks may draw: closer than
 * this, they were the same spot.
 */
export const PART_WALL_SNAP_PX = 10;

const halfLengthOf = (wall) => (wall?.dimensions?.x ?? 0) / 2;

const clampToWall = (t, wall) => {
  const half = halfLengthOf(wall);
  return Math.min(half, Math.max(-half, t));
};

/**
 * The positions along `structureWall` an end snaps to.
 *
 * @param {Object} structureWall
 * @param {Array} rooms - room list (their walls carry `alignment`)
 * @param {{doors?, windows?, openings?}} structureOpenings - the structure
 *   document's own records (they carry the geometry; room copies are slim)
 * @returns {number[]} t values, each within the wall's ends
 */
export function partWallSnapTargets(structureWall, rooms, structureOpenings) {
  if (!structureWall?.id || !structureWall.center) return [];
  const axis = getStructureWallAxis(structureWall);
  const half = halfLengthOf(structureWall);
  const ts = [-half, half];
  for (const { wall } of roomWallsForStructureWall(rooms, structureWall.id)) {
    const span = roomWallAxisSpan(wall, axis);
    if (span) ts.push(span.min, span.max);
  }
  for (const key of ['doors', 'windows', 'openings']) {
    for (const opening of structureOpenings?.[key] || []) {
      if (opening?.parentWallIds?.[0] !== structureWall.id) continue;
      if (!opening.center || !opening.dimensions) continue;
      const span = alongWallSpan(opening, axis.dir, axis.origin);
      ts.push(span.min, span.max);
    }
  }
  return ts.filter(Number.isFinite).map((t) => clampToWall(t, structureWall));
}

/**
 * Where on `wall` a world point falls: projected onto the wall's axis, held
 * between its ends, then snapped to the nearest target within `tolerance`
 * (metres).
 *
 * @param {{x, z}} point
 * @returns {number} t
 */
export function partWallPositionAt(point, wall, targets, tolerance) {
  const t = clampToWall(
    projectOntoAxis(point, getStructureWallAxis(wall)),
    wall
  );
  let best = t;
  let bestDistance = tolerance;
  for (const target of targets || []) {
    const distance = Math.abs(target - t);
    if (distance <= bestDistance) {
      best = target;
      bestDistance = distance;
    }
  }
  return best;
}

/**
 * The demoed-opening box between `t0` and `t1` on `wall` — the wall's full
 * height and thickness, centred between the two — or null when the two are
 * closer than `minLength` (metres): the same spot clicked twice draws no span.
 */
export function partWallSpan(wall, t0, t1, minLength) {
  const min = Math.min(t0, t1);
  const max = Math.max(t0, t1);
  if (!(max - min >= minLength) || max - min <= 0) return null;
  return spanToOpening(wall, getStructureWallAxis(wall), { min, max });
}

/** A zero-width mark at `t` on `wall` — where the first click lands or would. */
export function partWallPoint(wall, t) {
  return spanToOpening(wall, getStructureWallAxis(wall), { min: t, max: t });
}
